import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";

function Sheet(props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetPortal(props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    data-slot="sheet-overlay"
    className={cn(
      "fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

// Unlike the generic shadcn Sheet, this one doesn't ship a default width —
// it exists solely as the sidebar's mobile off-canvas panel, so the sidebar
// sets its own width via className rather than fighting a baked-in default.
const SheetContent = React.forwardRef(
  ({ className, children, side = "left", showCloseButton = true, ...props }, ref) => {
    const { language } = useLanguage();
    return (
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          ref={ref}
          data-slot="sheet-content"
          data-side={side}
          className={cn(
            "fixed inset-y-0 z-50 flex h-full flex-col bg-card text-sm text-foreground shadow-lg transition ease-in-out",
            "data-[state=open]:animate-in data-[state=open]:duration-300",
            "data-[state=closed]:animate-out data-[state=closed]:duration-200",
            side === "left" &&
              "left-0 border-r border-border data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
            side === "right" &&
              "right-0 border-l border-border data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
            className
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <SheetPrimitive.Close asChild>
              <Button variant="ghost" size="icon-sm" className="absolute end-3 top-3">
                <XIcon />
                <span className="sr-only">{translations[language].ui.close}</span>
              </Button>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  }
);
SheetContent.displayName = "SheetContent";

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    data-slot="sheet-title"
    className={cn("text-base font-medium text-foreground", className)}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    data-slot="sheet-description"
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";

export { Sheet, SheetContent, SheetTitle, SheetDescription };
